import React, { useState } from 'react';
import {
  Box, Flex, Heading, Text, SimpleGrid, Button, Card, CardBody, HStack, Alert, AlertIcon,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { Star, Library, Brain, BookOpen, Layers, FileText, Upload, LogIn } from 'lucide-react';
import Sidebar from '../components/Dashboard/Sidebar';
import { useAuth } from '../context/AuthContext';

// Open to everyone, including visitors who are not logged in
const PUBLIC_FEATURES = [
  { title: 'Resources', description: 'Expert-endorsed datasets, documents, recitations and links', icon: Library, color: 'teal', url: '/resources' },
  { title: 'AI Models', description: 'Quranic AI models to download and try', icon: Brain, color: 'purple', url: '/models' },
  { title: 'Read & listen by Surah', description: 'All 114 surahs with translation and recitation', icon: BookOpen, color: 'blue', url: '/surahs' },
  { title: 'Read by Juz', description: 'The Quran in its 30 parts, with recitation', icon: Layers, color: 'cyan', url: '/juz' },
  { title: 'Mushaf pages', description: 'Read page by page, like the printed mushaf', icon: FileText, color: 'orange', url: '/pages/1' },
];

// Need an account
const MEMBER_FEATURES = [
  { title: 'Share a resource', description: 'Upload a file or share a link for experts to endorse', icon: Upload, color: 'green', url: '/contribute' },
  { title: 'Upload a model', description: 'Share a trained Quranic AI model', icon: Brain, color: 'purple', url: '/models/upload' },
];

const FeatureCard = ({ feature }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');
  const Icon = feature.icon;
  return (
    <Card
      as={RouterLink} to={feature.url}
      bg={cardBg} variant="outline" borderRadius="lg" boxShadow="sm"
      transition="transform 0.3s, box-shadow 0.3s"
      _hover={{ transform: 'translateY(-5px)', boxShadow: 'md' }}
    >
      <CardBody p={5}>
        <Flex
          w="44px" h="44px" borderRadius="lg" align="center" justify="center" mb={4}
          bg={`${feature.color}.50`} color={`${feature.color}.500`}
        >
          <Icon size={22} />
        </Flex>
        <Heading size="sm" mb={1}>{feature.title}</Heading>
        <Text fontSize="sm" color={subtextColor}>{feature.description}</Text>
      </CardBody>
    </Card>
  );
};

const FeatureSection = ({ title, description, features }) => (
  <Box mb={10}>
    <Flex align="center" mb={2}>
      <Star size={20} fill="currentColor" color="gray.600" />
      <Heading size="lg" ml={2}>{title}</Heading>
    </Flex>
    <Text mb={6} color="gray.600">{description}</Text>
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
      {features.map((f) => <FeatureCard key={f.url} feature={f} />)}
    </SimpleGrid>
  </Box>
);

const Dashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user } = useAuth();

  return (
    <Flex direction="row" h="100vh">
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <Box
        pl={sidebarCollapsed ? '60px' : '250px'}
        w="full"
        overflowY="auto"
        pb={8}
        transition="padding-left 0.2s ease"
      >
        <Box p={8}>
          <Box mb={8}>
            <Heading size="xl" mb={2}>{user ? `Welcome back, ${user.name}!` : 'Welcome to Quranic DataHub'}</Heading>
            <Text color="gray.600">
              {user
                ? "You're on a roll! Jump back in, or start something new."
                : "You're browsing as a guest. Everything below is open to you."}
            </Text>
          </Box>

          {!user && (
            <Alert status="info" borderRadius="lg" mb={8}>
              <AlertIcon />
              <Flex w="full" justify="space-between" align="center" gap={4} wrap="wrap">
                <Text>Log in to share resources, upload models and try the AI model demos.</Text>
                <HStack>
                  <Button as={RouterLink} to="/login" size="sm" colorScheme="teal" leftIcon={<LogIn size={14} />}>Log in</Button>
                  <Button as={RouterLink} to="/register" size="sm" variant="outline" colorScheme="teal">Sign up</Button>
                </HStack>
              </Flex>
            </Alert>
          )}

          <FeatureSection
            title="Explore"
            description="Read, listen to and download Quranic data"
            features={PUBLIC_FEATURES}
          />

          {user && (
            <FeatureSection
              title="Contribute"
              description="Share your own work with the community"
              features={MEMBER_FEATURES}
            />
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default Dashboard;
